import { CardHeader,
  Drawer,
  useMediaQuery,
  Card,
  CardContent, Box, Button, FormHelperText, Grid, MenuItem, Stack } from "@mui/material";
import { useFormik } from "formik";
import { useGetMainList } from "ra-core";
import { FC, useCallback, useEffect, useRef } from "react";
import * as Yup from 'yup'
import OdForm, { OnSubmit } from "@/components/formik/OdForm";
import TextField from "@/components/formik/TextField";
import { RecPatch } from "@/dataProvider/tools";
import { OptionResponseDto } from "@/gen/api/user";
import { useNetworkConfigurationContext } from "@/pages/deployment/useNetworkConfigurationContext";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import SelectField from "../formik/SelectField";


export interface NewVPNGatewayProps {
  open: boolean;
  setOpen: (x:boolean) => void;
}

const validationSchema =
  Yup.object().shape({
    name: Yup.string().max(100).required('VPN Gateway Name is required')
      .matches(/^[a-zA-Z0-9_]+$/, 'The name may contain only letters, numbers or underscores'),
    performanceTier: Yup.object().required('Performance Tier is required'),
  })

const initialValues = {
  submit: '',
  name: '',
  performanceTier: '' as '' | OptionResponseDto,
}

export const NewVPNGateway: FC<NewVPNGatewayProps> = ({
  open,
  setOpen,
}) => {
  const { createGateway } = useNetworkConfigurationContext()

  const handleClose = (): void => {
    setOpen(false);
    resetForm({
      values: initialValues
    })
  };

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const { data: skuData } = useGetMainList<OptionResponseDto & RecPatch>('network-connection-sku', pages(), sorts(), {})
  const performanceTiers = Object.values(skuData || {}).sort((a, b) => a.id < b.id ? -1 : 1)

  const onSubmit: OnSubmit<typeof initialValues> = useCallback((values, {
    setStatus,
    setErrors,
    setSubmitting,
    resetForm,
  }) => {
    const options = {
      onSuccess: () => {
        resetForm({
          status: { success: true },
          values: initialValues,
          isSubmitting: false,
        })
        setOpen(false)
      },
      onFailure: (err: any) => {
        setStatus({ success: false })
        setErrors({ submit: err.message })
        setSubmitting(false)
      }
    }

    createGateway({ name: values.name, performanceTier: values.performanceTier ? (values.performanceTier.value || undefined) : undefined }, options)
  }, [createGateway])

  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema })
  const { values, errors, isSubmitting, resetForm } = formikCtrl
 

  return (
    <Drawer
      anchor="right"
      onClose={handleClose}
      open={open}
      PaperProps={{
        sx: {
          p: 2,
          width: width,
        }
      }}
    >
      <Card 
          sx={{ 
            overflowY: 'auto',
            mb: 4,
          }}
        >
        <CardHeader title="New VPN Gateway" />
        <CardContent>
          <OdForm formikCtrl={formikCtrl}>

            <Card>
              <CardContent>
                <Grid
                  container
                  spacing={4}
                >
                  <Grid item xs={12} >
                    <TextField label="VPN Gateway Name*" name="name" />
                  </Grid>
                  <Grid item xs={12} >
                    <SelectField label="Performance Tier*" name="performanceTier" >
                      {performanceTiers.map(t => (
                        <MenuItem key={t.id} value={(t || '') as any}>{t.displayName}</MenuItem>
                      ))}
                    </SelectField>
                    {!!values.performanceTier &&
                      <FormHelperText sx={{ mt: 3 }}>
                        {values.performanceTier.description}
                      </FormHelperText>}
                  </Grid>
                </Grid>
                {errors.submit && (
                  <Box sx={{ mt: 3 }}>
                    <FormHelperText error>
                      {errors.submit}
                    </FormHelperText>
                  </Box>
                )}
              </CardContent>
              <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }} >
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  variant="outlined"
                  onClick={handleClose}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={isSubmitting}
                  type="submit"
                  variant="contained"
                >
                  Deploy VPN Gateway
                </Button>
              </Stack>
            </Card>

          </OdForm>
        </CardContent>
      </Card>
    </Drawer>
  )
}


  



export default NewVPNGateway;
